import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { wizardStore } from '@/store/wizard.store';

/**
 * Copy per-fase dell'intro (port del legacy `phasesConfig`): titolo + "POTRAI PERSONALIZZARE" (3 bullet).
 * Le chiavi combaciano con i `phaseKey` del menu.
 */
export const PHASE_INTRO: Record<string, { title: string; descriptions: string[] }> = {
  Layout: {
    title: 'Layout',
    descriptions: ['La distribuzione interna', 'La tipologia di porte', 'La composizione della cucina'],
  },
  Tecnologia: {
    title: 'Tecnologia',
    descriptions: ["L'illuminazione", 'Le prese', 'Le funzionalità smart'],
  },
  GIORNOENOTTE: {
    title: 'Zona giorno e notte',
    descriptions: ['Il pavimento del soggiorno', 'Il pavimento delle camere', 'Il pavimento della cucina'],
  },
  BAGNO: {
    title: 'Bagno',
    descriptions: ['Il pavimento e i rivestimenti', 'Il mobile lavabo e i sanitari', 'La rubinetteria'],
  },
};

interface ConfiguratorIntroProps {
  configId: number;
}

const PhaseChevron = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'completed',
})<{ completed: boolean }>(({ theme, completed }) => ({
  position: 'relative',
  flex: 1,
  minWidth: 180,
  padding: theme.spacing(2.5, 2),
  borderRadius: theme.shape.borderRadius,
  border: `1px ${completed ? 'solid' : 'dashed'}`,
  borderColor: completed ? theme.palette.secondary.main : theme.palette.divider,
  backgroundColor: completed ? 'rgba(118,184,45,0.06)' : theme.palette.background.paper,
}));

/**
 * Schermata START/intro del configuratore (port del legacy PhaseListPage in modalità START).
 * Header motivazionale + timeline + elenco fasi con "POTRAI PERSONALIZZARE" + bottone START.
 */
/** Formatta una data ISO in "GG mese AAAA" (it-IT), o null se assente/non valida. */
function formatIntroDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

export const ConfiguratorIntro = observer(function ConfiguratorIntro({ configId }: ConfiguratorIntroProps) {
  const navigate = useNavigate();
  const phases = wizardStore.groupedMenu;
  // INIZIO = creationDate (ISO, da formattare). TERMINE = deadline: stringa di display già
  // formattata dal BE (hardcoded per progetto nel legacy, es. "28 marzo 2024") → mostrata as-is.
  const startDate = formatIntroDate(wizardStore.metadata?.creationDate);
  const deadlineDate = wizardStore.metadata?.deadline ?? null;

  const allSubsteps = wizardStore.menu;
  const answeredCount = allSubsteps.filter((s) => s.isAnswered).length;
  const allAnswered = allSubsteps.length > 0 && answeredCount === allSubsteps.length;
  const noneAnswered = answeredCount === 0;

  const ctaLabel = allAnswered
    ? 'Vai al riepilogo'
    : noneAnswered
      ? 'START! Inizia la personalizzazione!'
      : 'Continua la personalizzazione';

  const onCta = (): void => {
    if (allAnswered) navigate(`/configurations/${configId}/resume`);
    else void wizardStore.startConfiguration();
  };

  /** Una fase è completata se tutti i suoi substep sono risposti. */
  const isPhaseCompleted = (phaseKey: string): boolean => {
    const subs = allSubsteps.filter((s) => s.phaseKey === phaseKey);
    return subs.length > 0 && subs.every((s) => s.isAnswered);
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Header motivazionale (copy legacy). */}
      <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: 4 }}>
        <Typography sx={{ fontFamily: '"miller", serif', fontStyle: 'italic', fontSize: 24, lineHeight: 1.4 }}>
          <Box component="span" sx={{ color: 'primary.main' }}>
            In pochi semplici passi
          </Box>{' '}
          potrai personalizzare le finiture e i dettagli della tua casa!
        </Typography>

        <Button variant="contained" color="secondary" size="large" onClick={onCta} sx={{ mt: 3, px: 6, py: 1.5 }}>
          {ctaLabel}
        </Button>
      </Box>

      {/* Timeline INIZIO (creationDate) ··· TERMINE (scadenza progetto, null finché il BE non la espone). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          maxWidth: 900,
          mx: 'auto',
          mb: 4,
          color: 'text.secondary',
        }}
      >
        <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          {startDate && (
            <Typography sx={{ fontFamily: '"miller", serif', fontStyle: 'italic', color: 'primary.main' }}>
              {startDate}
            </Typography>
          )}
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            INIZIO CONFIGURAZIONE
          </Typography>
        </Box>
        <Box sx={{ flex: 1, borderBottom: '1px dashed', borderColor: 'divider', mt: 2 }} />
        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          {deadlineDate && (
            <Typography sx={{ fontFamily: '"miller", serif', fontStyle: 'italic', color: 'primary.main' }}>
              {deadlineDate}
            </Typography>
          )}
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            TERMINE MASSIMO PER LA CONFIGURAZIONE
          </Typography>
        </Box>
      </Box>

      {/* Elenco fasi con "POTRAI PERSONALIZZARE". */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ maxWidth: 1100, mx: 'auto' }}>
        {phases.map((phase) => {
          const copy = PHASE_INTRO[phase.phaseKey];
          const completed = isPhaseCompleted(phase.phaseKey);
          return (
            <PhaseChevron key={phase.phaseKey} completed={completed}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ textTransform: 'uppercase', fontSize: '1rem' }}>
                  {copy?.title ?? phase.phaseName}
                </Typography>
                {completed ? (
                  <CheckCircleIcon color="secondary" fontSize="small" aria-hidden="true" />
                ) : (
                  <Chip label={`Fase ${phase.phaseOrder ?? ''}`.trim()} size="small" variant="outlined" />
                )}
              </Box>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
                Potrai personalizzare
              </Typography>
              <Stack component="ul" sx={{ pl: 2, m: 0, mt: 0.5 }} spacing={0.25}>
                {(copy?.descriptions ?? []).map((d) => (
                  <Typography key={d} component="li" variant="body2" color="text.secondary">
                    {d}
                  </Typography>
                ))}
              </Stack>
            </PhaseChevron>
          );
        })}
      </Stack>
    </Box>
  );
});
